import React, { useState, useEffect } from 'react';
import './PostArticleship.css';
import { apiCall } from '../utils/api';
import trackEvent from '../utils/analytics';

const AREA_OPTIONS = [
    'Audit', 'Tax', 'Advisory', 'Risk & Forensics',
    'Corporate Finance', 'Transfer Pricing', 'Tax Litigation',
    'Compliance', 'GST', 'Internal Audit', 'Statutory Audit', 'Bank Audit'
];

const ARTICLE_PREFERENCE_OPTIONS = [
    { value: 'local', label: 'Local Article' },
    { value: 'immigrant', label: 'Immigrant Article' },
    { value: 'any', label: 'Any' }
];

const TOTAL_STEPS = 3;

const PostArticleship = () => {
    const [step, setStep] = useState(1);
    const [firmData, setFirmData] = useState(null);
    const [formData, setFormData] = useState({
        numberOfPositions: '',
        aboutJob: '',
        stipendFrom: '',
        stipendTo: '',
        needResume: 'mandatory',
        areasOfArticleship: [],
        articlePreference: 'any'
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState(null);
    const [formError, setFormError] = useState('');

    useEffect(() => {
        fetchFirmData();
    }, []);

    const fetchFirmData = async () => {
        try {
            const response = await apiCall.get('/firm/me');
            const data = response.data || response;
            if (data) {
                setFirmData(data);
            }
        } catch (error) {
            console.error('Error fetching firm data:', error);
        }
    };

    const handleInputChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        setFormError('');
    };

    const toggleArea = (area) => {
        setFormData(prev => ({
            ...prev,
            areasOfArticleship: prev.areasOfArticleship.includes(area)
                ? prev.areasOfArticleship.filter(a => a !== area)
                : [...prev.areasOfArticleship, area]
        }));
        setFormError('');
    };

    const validateStep = (s) => {
        if (s === 1) {
            if (!formData.numberOfPositions || parseInt(formData.numberOfPositions) < 1) {
                setFormError('Please enter number of positions');
                return false;
            }
            if (!formData.aboutJob.trim()) {
                setFormError('Please describe the job');
                return false;
            }
            return true;
        }
        if (s === 2) {
            if (!formData.stipendFrom || !formData.stipendTo) {
                setFormError('Please enter stipend range');
                return false;
            }
            if (parseInt(formData.stipendFrom) > parseInt(formData.stipendTo)) {
                setFormError('"From" should be less than "To"');
                return false;
            }
            return true;
        }
        if (s === 3) {
            if (formData.areasOfArticleship.length === 0) {
                setFormError('Select at least one area');
                return false;
            }
            return true;
        }
        return true;
    };

    const handleNext = () => {
        if (!validateStep(step)) return;
        setFormError('');
        setStep(prev => Math.min(prev + 1, TOTAL_STEPS));
    };

    const handleBack = () => {
        setFormError('');
        setStep(prev => Math.max(prev - 1, 1));
    };

    const handleSubmit = async () => {
        if (!validateStep(step)) return;

        setIsSubmitting(true);
        setFormError('');
        trackEvent('firm_post_articleship_attempted', {});

        try {
            const payload = {
                number_of_positions: parseInt(formData.numberOfPositions),
                about_job: formData.aboutJob,
                stipend_from: parseInt(formData.stipendFrom),
                stipend_to: parseInt(formData.stipendTo),
                resume_required: formData.needResume === 'mandatory',
                areas_of_articleship: formData.areasOfArticleship,
                article_preference: formData.articlePreference
            };

            const response = await apiCall.post('/jobs', JSON.stringify(payload));

            if (response.status) {
                setSubmitStatus('success');
                trackEvent('firm_post_articleship_success', {});
                setFormData({
                    numberOfPositions: '',
                    aboutJob: '',
                    stipendFrom: '',
                    stipendTo: '',
                    needResume: 'mandatory',
                    areasOfArticleship: [],
                    articlePreference: 'any'
                });
                setStep(1);
            } else {
                setFormError(response.message || 'Failed to post. Please try again.');
            }
        } catch (error) {
            console.error('Post articleship error:', error);
            setFormError('Failed to post. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (submitStatus === 'success') {
        return (
            <div className="pa-screen">
                <div className="pa-success">
                    <div className="pa-success-icon">✓</div>
                    <h2>Job Posted!</h2>
                    <p>Your articleship opening is now live. Candidates will start applying soon.</p>
                    <button
                        className="pa-btn pa-btn-primary"
                        onClick={() => setSubmitStatus(null)}
                    >
                        Post Another Opening
                    </button>
                </div>
            </div>
        );
    }

    const stepLabels = ['Job Details', 'Stipend & Resume', 'Areas'];

    return (
        <div className="pa-screen">
            {/* Green header with progress */}
            <div className="pa-header">
                <h1 className="pa-header-title">Post Articleship</h1>
                <div className="pa-progress-bar">
                    {Array.from({ length: TOTAL_STEPS }).map((_, i) => (
                        <div
                            key={i}
                            className={`pa-progress-segment ${i + 1 <= step ? 'active' : ''}`}
                        />
                    ))}
                </div>
                <p className="pa-step-label">Step {step} of {TOTAL_STEPS} — {stepLabels[step - 1]}</p>
            </div>

            {/* Form body */}
            <div className="pa-body">
                {/* Step 1 */}
                {step === 1 && (
                    <div className="pa-step">
                        <div className="pa-field">
                            <label>Number of Positions <span className="pa-req">*</span></label>
                            <div className="pa-input-wrap">
                                <input
                                    type="number"
                                    min="1"
                                    placeholder="e.g., 5"
                                    value={formData.numberOfPositions}
                                    onChange={(e) => handleInputChange('numberOfPositions', e.target.value)}
                                />
                            </div>
                        </div>

                        <div className="pa-field">
                            <label>About the Job <span className="pa-req">*</span></label>
                            <textarea
                                rows={4}
                                placeholder="Describe the role, responsibilities, and what you're looking for..."
                                value={formData.aboutJob}
                                onChange={(e) => handleInputChange('aboutJob', e.target.value)}
                            />
                        </div>
                    </div>
                )}

                {/* Step 2 */}
                {step === 2 && (
                    <div className="pa-step">
                        <div className="pa-field">
                            <label>Monthly Stipend Range (₹) <span className="pa-req">*</span></label>
                            <div className="pa-stipend-row">
                                <div className="pa-input-wrap pa-input-icon">
                                    <span className="pa-icon">₹</span>
                                    <input
                                        type="number"
                                        min="0"
                                        placeholder="From"
                                        value={formData.stipendFrom}
                                        onChange={(e) => handleInputChange('stipendFrom', e.target.value)}
                                    />
                                </div>
                                <span className="pa-stipend-dash">–</span>
                                <div className="pa-input-wrap pa-input-icon">
                                    <span className="pa-icon">₹</span>
                                    <input
                                        type="number"
                                        min="0"
                                        placeholder="To"
                                        value={formData.stipendTo}
                                        onChange={(e) => handleInputChange('stipendTo', e.target.value)}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="pa-field">
                            <label>Resume Requirement</label>
                            <div className="pa-toggle-row">
                                <button
                                    type="button"
                                    className={`pa-toggle-btn ${formData.needResume === 'mandatory' ? 'active' : ''}`}
                                    onClick={() => handleInputChange('needResume', 'mandatory')}
                                >
                                    Mandatory
                                </button>
                                <button
                                    type="button"
                                    className={`pa-toggle-btn ${formData.needResume === 'optional' ? 'active' : ''}`}
                                    onClick={() => handleInputChange('needResume', 'optional')}
                                >
                                    Optional
                                </button>
                            </div>
                        </div>

                        <div className="pa-field">
                            <label>Article Preference</label>
                            <div className="pa-toggle-row pa-toggle-row-3">
                                {ARTICLE_PREFERENCE_OPTIONS.map(opt => (
                                    <button
                                        key={opt.value}
                                        type="button"
                                        className={`pa-toggle-btn ${formData.articlePreference === opt.value ? 'active' : ''}`}
                                        onClick={() => handleInputChange('articlePreference', opt.value)}
                                    >
                                        {opt.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {/* Step 3 */}
                {step === 3 && (
                    <div className="pa-step">
                        <div className="pa-field">
                            <label>Areas of Articleship <span className="pa-req">*</span></label>
                            <p className="pa-hint">Select all that apply</p>
                            <div className="pa-checkbox-list">
                                {AREA_OPTIONS.map(area => (
                                    <label key={area} className="pa-checkbox">
                                        <input
                                            type="checkbox"
                                            checked={formData.areasOfArticleship.includes(area)}
                                            onChange={() => toggleArea(area)}
                                        />
                                        <span>{area}</span>
                                    </label>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {formError && <div className="pa-error">{formError}</div>}

                {/* Navigation buttons */}
                <div className="pa-actions">
                    {step > 1 && (
                        <button type="button" className="pa-btn pa-btn-back" onClick={handleBack}>
                            Back
                        </button>
                    )}
                    {step < TOTAL_STEPS ? (
                        <button type="button" className="pa-btn pa-btn-primary" onClick={handleNext}>
                            Continue
                        </button>
                    ) : (
                        <button
                            type="button"
                            className="pa-btn pa-btn-primary"
                            onClick={handleSubmit}
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? 'Posting...' : 'Post Opening'}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default PostArticleship;
